import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getSupabaseClient } from '../config/supabase';

export default function Admin() {
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState('');
  const [posts, setPosts] = useState<
    { id: number; title: string; slug: string; modified_gmt?: string; author?: { name?: string } | null }[]
  >([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const purgeCache = async () => {
    const apiKey = import.meta.env.VITE_EDGE_SYNC_API_KEY || '';
    if (!apiKey) return;
    try {
      await fetch('/api/cache-purge', {
        method: 'POST',
        headers: { 'x-api-key': apiKey },
      });
    } catch (err) {
      console.error('Falha ao limpar cache', err);
    }
  };

  const callSync = async (
    endpoint: 'sync-wp-drafts' | 'sync-wp-post' | 'generate-sitemap',
    slug?: string
  ) => {
    try {
      setSyncing(true);
      setMessage(null);
      const base = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '';
      const apiKey = import.meta.env.VITE_EDGE_SYNC_API_KEY || '';
      if (!base) throw new Error('Defina VITE_SUPABASE_FUNCTIONS_URL');
      if (!apiKey) throw new Error('Defina VITE_EDGE_SYNC_API_KEY');
      const supabase = getSupabaseClient();
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sessão inválida. Faça login novamente.');
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const root = base.endsWith('/') ? base.slice(0, -1) : base;
      const url =
        endpoint === 'sync-wp-post'
          ? `${root}/functions/v1/sync-wp-post?slug=${encodeURIComponent(slug || '')}`
          : endpoint === 'generate-sitemap'
            ? `${root}/functions/v1/generate-sitemap`
            : `${root}/functions/v1/sync-wp-drafts`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-api-key': apiKey,
          ...(anonKey ? { apikey: anonKey } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Erro HTTP ${res.status}`);
      setMessage(JSON.stringify(data));
      if (endpoint === 'sync-wp-drafts' || endpoint === 'sync-wp-post') {
        await loadPosts();
        await purgeCache();
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const loadPosts = async () => {
    try {
      setLoadingPosts(true);
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('wp_posts')
        .select('id, title, slug, modified_gmt, author')
        .order('modified_gmt', { ascending: false });
      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error(err);
      setMessage(err instanceof Error ? err.message : 'Erro ao carregar posts');
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleDeletePost = async (postId: number, slug: string) => {
    const ok = window.confirm(`Deseja remover o post "${slug}"?`);
    if (!ok) return;
    try {
      setSyncing(true);
      setDeletingId(postId);
      setMessage(null);
      const base = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || '';
      const apiKey = import.meta.env.VITE_EDGE_SYNC_API_KEY || '';
      if (!base) throw new Error('Defina VITE_SUPABASE_FUNCTIONS_URL');
      if (!apiKey) throw new Error('Defina VITE_EDGE_SYNC_API_KEY');
      const root = base.endsWith('/') ? base.slice(0, -1) : base;
      const url = `${root}/functions/v1/delete-post?id=${encodeURIComponent(postId)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Erro HTTP ${res.status}`);
      setMessage('Post removido com sucesso.');
      await loadPosts();
      await purgeCache();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Erro ao remover post');
    } finally {
      setSyncing(false);
      setDeletingId(null);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  return (
    <div className="bg-[#081428] text-white min-h-screen">
      <SEO title="Admin - Blog LawX" description="Sincronizar conteúdo do WordPress" url="/admin" />
      <Navbar />

      <main className="container mx-auto px-6 pt-28 pb-16">
        <h1 className="text-3xl font-bold mb-6">Admin - Sincronização</h1>

        <div className="space-y-6">
          <section className="bg-[#0a1a2e] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Sincronizar drafts</h2>
            <p className="text-gray-400 mb-4">Roda /wp-json/my/v1/drafts e upserta posts, categorias, tags.</p>
            <button
              onClick={() => callSync('sync-wp-drafts')}
              disabled={syncing}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60"
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar drafts'}
            </button>
          </section>

          <section className="bg-[#0a1a2e] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Sincronizar post específico</h2>
            <p className="text-gray-400 mb-4">Use o slug (ou ID) do post no WP.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="slug-do-post"
                className="flex-1 px-4 py-2 rounded-lg bg-[#0b1830] border border-white/10 text-white"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
              />
              <button
                onClick={() => callSync('sync-wp-post', slugInput)}
                disabled={syncing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-60"
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar post'}
              </button>
            </div>
          </section>

          <section className="bg-[#0a1a2e] border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-2">Gerar sitemap.xml</h2>
            <p className="text-gray-400 mb-4">
              Gera o sitemap com base em wp_posts e substitui o arquivo público.
            </p>
            <button
              onClick={() => callSync('generate-sitemap')}
              disabled={syncing}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-60"
            >
              {syncing ? 'Gerando...' : 'Gerar sitemap'}
            </button>
          </section>

          <section className="bg-[#0a1a2e] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Posts</h2>
              <button
                onClick={loadPosts}
                disabled={loadingPosts}
                className="px-3 py-1 text-sm bg-[#0b1830] border border-white/10 rounded-lg text-gray-200 hover:border-white/20 disabled:opacity-60"
              >
                {loadingPosts ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="text-gray-400 border-b border-white/10">
                  <tr>
                    <th className="py-2 pr-4">Título</th>
                    <th className="py-2 pr-4">Slug</th>
                    <th className="py-2 pr-4">Autor</th>
                    <th className="py-2 pr-4">Atualizado</th>
                    <th className="py-2 pr-4">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {posts.length === 0 && (
                    <tr>
                      <td className="py-3 text-gray-500" colSpan={5}>
                        {loadingPosts ? 'Carregando...' : 'Nenhum post encontrado.'}
                      </td>
                    </tr>
                  )}
                  {posts.map((post) => (
                    <tr key={post.id} className="text-gray-200">
                      <td className="py-3 pr-4">{post.title}</td>
                      <td className="py-3 pr-4">{post.slug}</td>
                      <td className="py-3 pr-4">{post.author?.name || '-'}</td>
                      <td className="py-3 pr-4">
                        {post.modified_gmt ? new Date(post.modified_gmt).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-3 pr-4">
                        <button
                          onClick={() => handleDeletePost(post.id, post.slug)}
                          disabled={syncing || deletingId === post.id}
                          className="px-3 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {deletingId === post.id ? 'Excluindo...' : 'Remover'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {message && (
            <div className="bg-[#0b1830] border border-white/10 rounded-xl p-4 text-sm text-gray-200 whitespace-pre-wrap break-words">
              {message}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
